<template>
  <v-container>
    <v-row>
      <v-col cols="12">
        <div class="text-overline">Queue</div>
      </v-col>
    </v-row>
    <v-row class="text-center mx-auto">
      <v-col class="mb-4" cols="12">
        <v-col
          cols="12"
          v-if="queueInitialLoading"
          style="height: 50vh"
          class="d-flex justify-center align-center"
        >
          <v-progress-circular
            :size="50"
            class="m"
            indeterminate
            color="primary"
          >
          </v-progress-circular>
        </v-col>

        <v-expansion-panels
          v-model="panel"
          multiple
          v-if="!queueInitialLoading"
        >
          <!-- Expansion panel for assigned reviews -->
          <v-expansion-panel>
            <v-expansion-panel-header>
              Assigned to you:
              <div class="ml-2">
                <v-progress-circular
                  v-if="assignedRevLoading"
                  :size="20"
                  indeterminate
                  color="primary"
                ></v-progress-circular>
                <v-chip v-if="!assignedRevLoading" color="primary" small>
                  {{ assignedItems.length }}
                </v-chip>
              </div>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <div v-if="assignedRevErrorMsg">
                <v-col
                  cols="12"
                  class="d-flex justify-center align-center mb-5 mt-5"
                >
                  <v-row>
                    <v-col
                      cols="12"
                      class="d-flex justify-center align-center ma-0 pa-0"
                    >
                      <v-icon x-large color="red darken-2">
                        mdi-information-slab-circle-outline
                      </v-icon>
                    </v-col>
                    <v-col
                      cols="12"
                      class="d-flex justify-center align-center ma-0 pa-0"
                    >
                      {{ assignedRevErrorMsg }}
                    </v-col>
                  </v-row>
                </v-col>
              </div>
              <div v-if="!assignedRevErrorMsg">
                <v-data-table
                  :headers="assignedHeaders"
                  :items="assignedItems"
                  :items-per-page="5"
                  :loading="assignedRevLoading"
                  hide-default-footer
                  class="elevation-1"
                >
                  <template v-slot:item.Id="{ item }">
                    {{ $utils.shortenRevId(item.Id) }}
                  </template>
                  <template v-slot:item.Tier="{ item }">
                    <v-chip
                      :color="$consts.TIER_MAP.get(item.Tier).color"
                      text-color="white"
                      small
                    >
                      {{ $consts.TIER_MAP.get(item.Tier).name }}
                    </v-chip>
                  </template>
                  <template v-slot:item.LastActionTs="{ item }">
                    {{ $utils.relativeTimeFromTs(item.LastActionTs) }}
                  </template>
                  <template v-slot:item.Status="{ item }">
                    <v-icon color="orange" x-small class="mr-1">
                      mdi-circle
                    </v-icon>
                    <span class="d-inline">{{
                      item.Status === "inreview" ? "Pending" : "b"
                    }}</span>
                  </template>
                  <template v-slot:item.Companies="{ item }">
                    {{ item.Companies.map((c) => c.Name).join(", ") }}
                  </template>
                  <template v-slot:item.Rate="{ item }">
                    <v-chip color="" small>
                      ${{ $consts.TIER_MAP.get(item.Tier).rate }}
                    </v-chip>
                  </template>
                  <template v-slot:item.Buttons="{ item }">
                    <v-btn
                      color="primary"
                      class="ma-2 float-right"
                      :to="getReviewLink(item.Id)"
                    >
                      Review
                    </v-btn>
                  </template>
                </v-data-table>
              </div>
            </v-expansion-panel-content>
          </v-expansion-panel>

          <!-- Error msg if user has no companies assigned - they can't review anything  -->
          <v-col
            cols="12"
            v-if="reviewerHasNoCompanies"
            style="height: 50vh"
            class="d-flex justify-center align-center"
          >
            <v-row>
              <v-col
                cols="12"
                class="d-flex justify-center align-center ma-0 pa-0"
              >
                <v-icon x-large color="blue darken-2">
                  mdi-information-slab-circle-outline
                </v-icon>
              </v-col>
              <v-col
                cols="12"
                class="d-flex justify-center align-center ma-0 pa-0"
              >
                You don't seem to have right to review any companies - please
                make sure your reviewer experience is up to date
              </v-col>
              <v-col cols="12" class="d-flex justify-center align-center mt-2">
                <v-btn large color="primary" to="/settings">
                  Go to settings
                </v-btn>
              </v-col>
            </v-row>
          </v-col>

          <!-- Expansion panel for queue reviews -->
          <v-expansion-panel v-if="!reviewerHasNoCompanies">
            <v-expansion-panel-header>Queue</v-expansion-panel-header>
            <v-expansion-panel-content>
              <div v-if="queueErrorMsg">
                <v-col
                  cols="12"
                  class="d-flex justify-center align-center mb-5 mt-5"
                >
                  <v-row>
                    <v-col
                      cols="12"
                      class="d-flex justify-center align-center ma-0 pa-0"
                    >
                      <v-icon x-large color="red darken-2">
                        mdi-information-slab-circle-outline
                      </v-icon>
                    </v-col>
                    <v-col
                      cols="12"
                      class="d-flex justify-center align-center ma-0 pa-0"
                    >
                      {{ queueErrorMsg }}
                    </v-col>
                  </v-row>
                </v-col>
              </div>
              <div v-if="!queueErrorMsg">
                <div class="d-flex flex-row mb-3">
                  <label class="mr-2">Tier filter:</label>
                  <v-chip
                    class="mr-2"
                    :color="
                      queueFilterTier1 ? $consts.TIER_MAP.get(1).color : 'gray'
                    "
                    :text-color="queueFilterTier1 ? 'white' : 'black'"
                    @click="changeTierFilter(1)"
                    small
                  >
                    {{ $consts.TIER_MAP.get(1).name }}
                  </v-chip>
                  <v-chip
                    class="mr-2"
                    :color="
                      queueFilterTier5 ? $consts.TIER_MAP.get(5).color : 'gray'
                    "
                    :text-color="queueFilterTier5 ? 'white' : 'black'"
                    @click="changeTierFilter(5)"
                    small
                  >
                    {{ $consts.TIER_MAP.get(5).name }}
                  </v-chip>
                  <v-chip
                    class="mr-2"
                    :color="
                      queueFilterTier10
                        ? $consts.TIER_MAP.get(10).color
                        : 'gray'
                    "
                    :text-color="queueFilterTier10 ? 'white' : 'black'"
                    @click="changeTierFilter(10)"
                    small
                  >
                    {{ $consts.TIER_MAP.get(10).name }}
                  </v-chip>
                </div>
                <v-data-table
                  :headers="queueHeaders"
                  :items="queueItems"
                  :items-per-page="15"
                  :loading="queueLoading"
                  hide-default-footer
                  loading-text="Loading..."
                  class="elevation-1"
                  disabled
                >
                  <template v-slot:item.Id="{ item }">
                    {{ $utils.shortenRevId(item.Id) }}
                  </template>
                  <template v-slot:item.Tier="{ item }">
                    <v-chip
                      :color="$consts.TIER_MAP.get(item.Tier).color"
                      text-color="white"
                      small
                    >
                      {{ $consts.TIER_MAP.get(item.Tier).name }}
                    </v-chip>
                  </template>
                  <template v-slot:item.Rate="{ item }">
                    <v-chip color="" small>
                      ${{ $consts.TIER_MAP.get(item.Tier).rate }}
                    </v-chip>
                  </template>
                  <template v-slot:item.Companies="{ item }">
                    {{ item.Companies.map((c) => c.Name).join(", ") }}
                  </template>
                  <template v-slot:item.UploadedTs="{ item }">
                    {{ $utils.relativeTimeFromTs(item.UploadedTs) }}
                  </template>
                  <template v-slot:item.Buttons="{ item }">
                    <v-btn
                      color="primary"
                      class="ma-2 float-right"
                      @click="assignReview(item.Id)"
                      :loading="item.Id == assigningId"
                      :disabled="queueDisabled"
                    >
                      Assign to me
                    </v-btn>
                  </template>
                </v-data-table>
              </div>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-col>
    </v-row>
    <!-- <v-row class="text-center mx-auto" v-if="queueLoading">
      <v-col
        class="mb-4"
        cols="12"
        sm="12"
        md="6"
        offset-md="2"
        order="2"
        order-md="1"
      >
        <v-col cols="12" v-for="index in 5" :key="index">
          <div>
            <v-spacer></v-spacer>
            <v-skeleton-loader type="heading"> </v-skeleton-loader>
            <v-spacer></v-spacer>
          </div>
        </v-col>
      </v-col>
    </v-row> -->
    <v-snackbar
      v-model="snackbar"
      :timeout="snackbarTimeout"
      content-class="orange--text"
    >
      {{ snackbarText }}
      <template v-slot:action="{ attrs }">
        <v-btn color="primary" text v-bind="attrs" @click="snackbar = false">
          Close
        </v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
export default {
  data() {
    return {
      queueItems: [],
      queueLoading: false,
      lastReviewKey: "",
      snackbar: false,
      snackbarText: "",
      snackbarTimeout: 5000,
      queueFilterTier1: true,
      queueFilterTier5: true,
      queueFilterTier10: true,
      tiersFilter: [1, 5, 10],
      panel: [1],
      assigningId: "",
      assignedHeaders: [
        { text: "Id", value: "Id" },
        { text: "Type", value: "Tier" },
        { text: "Last action", value: "LastActionTs" },
        { text: "Status", value: "Status" },
        { text: "Target company", value: "Companies" },
        { text: "Rate", value: "Rate" },
        { text: "", value: "Buttons" },
      ],
      assignedItems: [],
      queueHeaders: [
        { text: "Id", value: "Id" },
        { text: "Type", value: "Tier" },
        { text: "Posted", value: "UploadedTs" },
        { text: "Target company", value: "Companies" },
        { text: "Rate", value: "Rate" },
        { text: "", value: "Buttons" },
      ],
      queueItems: [],
      queueDisabled: false,
      killQueueCheck: false,
      reviewerHasNoCompanies: false,
      queueInitialLoading: true,
      queueErrorMsg: "",
      assignedRevLoading: true,
      assignedRevErrorMsg: "",
      getReviewsErrCount: 0,
    };
  },
  methods: {
    async getAssignedReviews() {
      try {
        this.assignedRevLoading = true;
        this.assignedRevErrorMsg = "";
        var resp = await this.$utils.callBackendPost(
          this,
          "/rec/getAssignedReviews",
          {}
        );
        this.assignedItems = resp;
      } catch (error) {
        this.assignedRevErrorMsg = error;
      } finally {
        this.assignedRevLoading = false;
      }
    },
    async getReviews() {
      try {
        this.queueErrorMsg = "";
        this.queueLoading = true;
        var resp = await this.$utils.callBackendPost(this, "/rec/getReviews", {
          lastKey: this.lastReviewKey,
          companies: [],
          tiers: this.tiersFilter, // 1, 5, 10
        });
        this.getReviewsErrCount = 0;
        if (resp == null) {
          this.queueItems = [];
          return;
        }
        this.queueItems = resp;
      } catch (error) {
        this.queueErrorMsg = error;
        this.getReviewsErrCount++;
      } finally {
        this.queueLoading = false;
        this.queueInitialLoading = false;
      }
    },
    async assignReview(revId) {
      try {
        // user reached max number of assigned reviews
        if (this.assignedItems.length >= 3) {
          throw "Max number of assigned reviews is 3";
        }
        // hack to set loading state for button
        this.assigningId = revId;
        var resp = await this.$utils.callBackendPost(
          this,
          "/rec/assignReview",
          {
            reviewId: revId,
          }
        );
        this.queueItems = this.queueItems.filter((i) => i.Id != revId);
        await this.getAssignedReviews();
      } catch (error) {
        this.snackbarText = error;
        this.snackbar = true;
      } finally {
        this.assigningId = "";
      }
    },
    async refreshQueue() {
      if (this.killQueueCheck || this.getReviewsErrCount > 5) {
        console.log("killing queue check");
        return;
      }
      try {
        console.log("refreshing queue...");
        this.queueDisabled = true;
        this.queueLoading = true;
        if (document.visibilityState === "visible") {
          await this.getReviews();
        }
      } finally {
        this.queueDisabled = false;
        this.queueLoading = false;
        setTimeout(this.refreshQueue, this.whenNextQueueRefresh());
      }
    },
    whenNextQueueRefresh() {
      // check less often if there's anything in the queue - user has something to choose from
      if (this.queueItems.length > 0) {
        return 60000; // 1 minute
      }
      // TODO: check less often if page is not focused (document.hasFocus()) ?
      return 30000; // 30 sec by default
    },
    changeTierFilter(tier) {
      switch (tier) {
        case 1:
          this.queueFilterTier1 = !this.queueFilterTier1;
          break;
        case 5:
          this.queueFilterTier5 = !this.queueFilterTier5;
          break;
        case 10:
          this.queueFilterTier10 = !this.queueFilterTier10;
          break;
      }
      this.tiersFilter = [];
      if (this.queueFilterTier1) {
        this.tiersFilter.push(1);
      } else {
        this.queueItems = this.queueItems.filter((i) => i.Tier !== 1);
      }
      if (this.queueFilterTier5) {
        this.tiersFilter.push(5);
      } else {
        this.queueItems = this.queueItems.filter((i) => i.Tier !== 5);
      }
      if (this.queueFilterTier10) {
        this.tiersFilter.push(10);
      } else {
        this.queueItems = this.queueItems.filter((i) => i.Tier !== 10);
      }
      this.refreshQueue();
    },
    getReviewLink(reviewId) {
      return "/review?id=" + reviewId;
    },
  },
  mounted() {
    this.$utils.visit(this);
  },
  async created() {
    this.killQueueCheck = false;
    await this.refreshQueue();
    await this.getAssignedReviews();
  },
  destroyed() {
    console.log("destroyed");
    this.killQueueCheck = true;
  },
};
</script>

<style>
.left-border-orange {
  border-left: 20px solid orange !important;
}
.left-border-blue {
  border-left: 20px solid blue !important;
}
.left-border-purple {
  border-left: 20px solid purple !important;
}
</style>
